import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { AutenticacionService } from '../../core/services/autenticacion.service';
import { esRutaPublicaAuth } from '../../core/constants/rutas-publicas.const';
import { AuthStore } from '../../store/auth.store';
import { environment } from '../../../environments/environment';
import { NetworkService } from './network.service';
import { ToastService } from './toast.service';

const EMBED_SCRIPT_ID = 'asistente-ia-embed-script';

/**
 * Monta el widget empotrable ia-demo (embed.js) cuando
 * `environment.asistenteIaEmbed.enabled` es true y hay sesión autenticada en línea.
 * No se monta en rutas públicas de auth (p. ej. /login).
 */
@Injectable({ providedIn: 'root' })
export class AsistenteIaEmbedService implements OnDestroy {
  private readonly autenticacionService = inject(AutenticacionService);
  private readonly authStore = inject(AuthStore);
  private readonly networkService = inject(NetworkService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  private readonly subs = new Subscription();
  private scriptLoadPromise: Promise<void> | null = null;
  private montado = false;
  private activando = false;
  private activacionSeq = 0;
  private falloNotificado = false;
  /** Listener de postMessage para registrar actividad del asistente IA en el contador de inactividad. */
  private readonly widgetMessageListener = (event: MessageEvent): void => {
    // Solo mensajes del origen del widget y cuando hay sesión activa.
    const origenPermitido = environment.asistenteIaEmbed.embedScriptUrl
      ? new URL(environment.asistenteIaEmbed.embedScriptUrl).origin
      : null;
    if (origenPermitido && event.origin !== origenPermitido) {
      return;
    }
    // Registrar interacción ante cualquier mensaje proveniente del widget.
    if (this.authStore.obtenerEstadoAutenticacion()) {
      this.authStore.registrarInteraccion();
    }
  };

  constructor() {
    this.subs.add(
      combineLatest([
        this.autenticacionService.usuarioActual$.pipe(
          startWith(this.autenticacionService.obtenerUsuarioActual())
        ),
        this.authStore.perfilActivo$.pipe(startWith(this.authStore.obtenerPerfilActivo())),
        this.networkService.networkStatus$.pipe(startWith(this.networkService.isOnline())),
      ]).subscribe(() => this.sincronizar())
    );
    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.sincronizar())
    );
  }

  ngOnDestroy(): void {
    this.desactivar();
    this.subs.unsubscribe();
    window.removeEventListener('message', this.widgetMessageListener);
  }

  /** Reevalúa si el embed debe montarse o desmontarse (sesión, red o perfil). */
  sincronizar(): void {
    if (this.debeMontar()) {
      if (this.montado) {
        this.inicializarWidget();
      } else {
        void this.activar();
      }
    } else {
      this.desactivar();
    }
  }

  private debeMontar(): boolean {
    const ruta = this.router.url.split('?')[0];
    return (
      environment.asistenteIaEmbed.enabled &&
      this.authStore.sesionValida() &&
      this.networkService.isOnline() &&
      !esRutaPublicaAuth(ruta)
    );
  }

  private async activar(): Promise<void> {
    if (this.montado || this.activando || !this.debeMontar()) {
      return;
    }

    const seq = ++this.activacionSeq;
    this.activando = true;

    try {
      await this.cargarScript();
      if (seq !== this.activacionSeq || !this.debeMontar() || this.montado) {
        return;
      }
      this.inicializarWidget();
      if (!window.AsistenteIA?._button) {
        this.registrarFallo('embed.js cargó pero no montó el botón flotante.');
        return;
      }
      this.montado = true;
      // Registrar interacciones del widget en el contador de inactividad.
      window.addEventListener('message', this.widgetMessageListener);
    } catch (err) {
      this.registrarFallo('No se pudo cargar el widget ia-demo (embed.js).', err);
    } finally {
      this.activando = false;
    }
  }

  private desactivar(): void {
    this.activacionSeq += 1;
    this.falloNotificado = false;
    if (!this.montado && !window.AsistenteIA?._button) {
      return;
    }
    const api = window.AsistenteIA;
    if (api) {
      api.close();
      api._button?.remove();
      api._panel?.remove();
      api._button = null;
      api._panel = null;
    }
    window.removeEventListener('message', this.widgetMessageListener);
    this.montado = false;
  }

  private registrarFallo(mensaje: string, err?: unknown): void {
    if (!environment.production) {
      console.warn(`[AsistenteIaEmbed] ${mensaje}`, err ?? '');
    }
    if (this.falloNotificado) {
      return;
    }
    this.falloNotificado = true;
    void this.toastService.mostrarError(mensaje);
  }

  private limpiarScriptFallido(): void {
    this.scriptLoadPromise = null;
    document.getElementById(EMBED_SCRIPT_ID)?.remove();
    window.AsistenteIA = undefined;
  }

  private cargarScript(): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.reject(new Error('DOM no disponible'));
    }

    if (window.AsistenteIA) {
      return Promise.resolve();
    }

    const existente = document.getElementById(EMBED_SCRIPT_ID) as HTMLScriptElement | null;
    if (existente) {
      if (!this.scriptLoadPromise) {
        this.scriptLoadPromise = new Promise((resolve, reject) => {
          if (window.AsistenteIA) {
            resolve();
            return;
          }
          existente.addEventListener('load', () => resolve(), { once: true });
          existente.addEventListener('error', () => {
            this.limpiarScriptFallido();
            reject(new Error('Error cargando embed.js'));
          }, { once: true });
        });
      }
      return this.scriptLoadPromise;
    }

    const cfg = environment.asistenteIaEmbed;
    this.scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = EMBED_SCRIPT_ID;
      script.src = cfg.embedScriptUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.limpiarScriptFallido();
        reject(new Error(`No se pudo cargar ${cfg.embedScriptUrl}`));
      };
      document.body.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  private inicializarWidget(): void {
    const cfg = environment.asistenteIaEmbed;
    const token = this.authStore.obtenerToken();
    const usuario = this.authStore.obtenerUsuarioActual();
    const usuarioId = usuario?.sub?.trim();
    const rol = this.authStore.obtenerPerfilActivo()?.descripcion?.trim();

    window.AsistenteIA?.init({
      apiBase: cfg.apiBase,
      apiKey: cfg.apiKey,
      tenant: cfg.tenant,
      title: cfg.title,
      primaryColor: cfg.primaryColor,
      headerColor: cfg.headerColor,
      userBubbleColor: cfg.userBubbleColor,
      launcherIcon: cfg.launcherIcon,
      ...(token ? { token } : {}),
      ...(usuarioId ? { usuarioId } : {}),
      ...(rol ? { rol } : {}),
    });
  }
}
